/**
 * Befundzeilen und „gegen typisch" des Kapitels „Webinare & Produkte" und der Webinar-Detailseite (Persona-Umbau B,
 * Plan 2026-10-28-persona-umbau-paket-1-2, Leitregeln 1/3/4). Rein und DOM-frei, getestet in `befunde.test.ts`.
 * Jeder Satz wird aus den Daten der Karte gerechnet und beschreibt nur — keine Ursache. Schätzungen heißen so.
 */
import type { EffizienzTeil, EffizienzZeile, ProduktZeile, TypischerTermin, UpsellAntwort, WebinarZeile } from '../../../helpers/auswertungenData';

const de0 = (v: number) => v.toLocaleString('de-DE', { maximumFractionDigits: 0 });
const de1 = (v: number) => v.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const tE = (v: number) => `${de1(v / 1000)} T€`;
/** € je 1.000 nach Regel 7: ab 10 € ohne Cent, darunter eine Stelle. */
export const euro1k = (v: number) => (Math.abs(v) >= 10 ? `${de0(v)} €` : `${de1(v)} €`);
const tm = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}.`;

// ─── gegen typisch (B1, B9) ─────────────────────────────────────────────────────────────────

/** Ab diesem Abstand steht ▲/▼ (darunter „≈ typisch"): relativ 5 %, beim Show-up 2 Prozentpunkte. */
export const SCHWELLE_RELATIV = 5;
export const SCHWELLE_PP = 2;

export interface Abstand {
  richtung: 'up' | 'down' | 'gleich';
  /** „+30 %", „−3 Prozentpunkte", „±0 %". */
  text: string;
}

/** Abstand eines Termins zu „typisch"; null ohne beide Werte (bzw. relativ ohne positiven Vergleich). */
export function abstandTypisch(wert: number | null | undefined, typisch: number | null | undefined, art: 'relativ' | 'pp' = 'relativ'): Abstand | null {
  if (wert === null || wert === undefined || typisch === null || typisch === undefined) return null;
  if (art === 'pp') {
    const d = wert - typisch;
    const r = Math.round(d);
    // C2 (Audit 30.10.): Kachel-Unterzeilen und Befunde schreiben das Wort aus; „Pp" nur in Tabellenzellen.
    const wort = Math.abs(r) === 1 ? 'Prozentpunkt' : 'Prozentpunkte';
    return { richtung: d >= SCHWELLE_PP ? 'up' : d <= -SCHWELLE_PP ? 'down' : 'gleich', text: `${r > 0 ? '+' : r < 0 ? '−' : '±'}${de0(Math.abs(r))} ${wort}` };
  }
  if (!(typisch > 0)) return null;
  const d = (wert / typisch - 1) * 100;
  const r = Math.round(d);
  return { richtung: d >= SCHWELLE_RELATIV ? 'up' : d <= -SCHWELLE_RELATIV ? 'down' : 'gleich', text: `${r > 0 ? '+' : r < 0 ? '−' : '±'}${de0(Math.abs(r))} %` };
}

// ─── Webinare der letzten 12 Monate (B4) ────────────────────────────────────────────────────

/** Unter so vielen Anfos über die Einladung ist der Wert je 1.000 grau (gleiche Schwelle wie die Tabelle). */
export const MIN_ANFOS_WERT = 20;

/** Kurzer Titel für Fließtext: Haupttitel vor „ - ", „ | " bzw. „: ", höchstens 32 Zeichen. */
export function kurzTitel(t: string, max = 32): string {
  const haupt = (t.split(/\s[-–|]\s|\s\|\s?/)[0] ?? t).trim();
  return haupt.length > max ? `${haupt.slice(0, max - 1).trimEnd()}…` : haupt;
}

/** Termine, die im Top/Flop zählen: Kontaktzahl, fertige Zahlen, Wert je 1.000 vorhanden, mind. 20 Anfos über die Einladung. */
export const zaehltJe1k = (r: WebinarZeile) =>
  !r.is_young && r.contacts > 0 && r.wert_je_1k_fest !== null && r.wert_je_1k_fest !== undefined && (r.send_anfos_fest ?? r.send_anfos) >= MIN_ANFOS_WERT;

/** „Je 1.000 Kontakte vorn: … — hinten: … (typisch 17 €, 31 Termine mit fertigem Wert)". null unter 6 Terminen. */
export function topFlopBefund(rows: WebinarZeile[], typisch: TypischerTermin | null | undefined): string | null {
  const z = rows.filter(zaehltJe1k).sort((a, b) => (b.wert_je_1k_fest as number) - (a.wert_je_1k_fest as number));
  if (z.length < 6) return null;
  const teil = (r: WebinarZeile) => `${kurzTitel(r.public_title)} (${tm(r.start_date)}) ${euro1k(r.wert_je_1k_fest as number)}`;
  const top = z.slice(0, 3).map(teil).join(', ');
  const flop = z.slice(-3).reverse().map(teil).join(', ');
  const t = typisch?.value_per_1k;
  return `Erwarteter Wert je 1.000 Kontakte — vorn: ${top}; hinten: ${flop}${t !== null && t !== undefined ? ` (typisch ${euro1k(t)})` : ''}. Gezählt ${z.length} Termine mit fertigen Zahlen und mindestens ${MIN_ANFOS_WERT} Anfos über die Einladung.`;
}

// ─── Produkte (B5) ──────────────────────────────────────────────────────────────────────────

/** Artikelnummer vorn im Namen entfernen (Audit C3): „2010005 DKD Einmalfaktura" → „DKD Einmalfaktura". */
export const ohneArtikelnummer = (name: string): string => name.replace(/^\d{5,}\s*[·:–-]?\s*/, '').trim() || name;

/** Name statt Artikelnummer (2010006 → „Die Depot-Architekten - Chancenpaket"), sonst das Kürzel. */
export const produktLabel = (code: string, name: string | null | undefined): string => (/^\d/.test(code) && name ? ohneArtikelnummer(name) : code);

/**
 * Produktnennung in einer Befundzeile (Audit C3, 30.10.2026): beim ersten Auftreten „Name (Kürzel)", danach nur das
 * Kürzel — „KBO" allein sagt Lesern außerhalb des Produktteams nichts. Artikelnummern erscheinen nie, nur der Name. Je
 * Befundzeile einen neuen Nenner anlegen.
 */
export function produktNenner(): (code: string, name: string | null | undefined) => string {
  const genannt = new Set<string>();
  return (code, name) => {
    if (/^\d/.test(code)) return produktLabel(code, name);
    const n = name ? ohneArtikelnummer(name) : null;
    const erstes = !genannt.has(code);
    genannt.add(code);
    return erstes && n && n !== code ? `${n} (${code})` : code;
  };
}

const imAbschnitt = (r: ProduktZeile) => !!r.ist_einmalkauf || !!r.nachfolger;
const pct0 = (v: number) => `${de0(v)} %`;

/**
 * „KBO: Ø 398 € je Anfo (2,2× Schnitt 180 €), Bezahlquote 56 % ▲. Auffällig schlechter zahlt: FMM 18 % ▼." — nur
 * laufende Produkte mit mindestens `minAnfos` Anfos mit Wert; ▲/▼ sind die geprüften Marken der Tabelle.
 */
export function produkteBefund(rows: ProduktZeile[], schnittJeAnfo: number | null | undefined, minAnfos = 20): string | null {
  const z = rows.filter((r) => !imAbschnitt(r) && (r.with_value ?? 0) >= minAnfos && r.value_per_anfo !== null && r.value_per_anfo !== undefined);
  if (!z.length || !schnittJeAnfo) return null;
  const top = [...z].sort((a, b) => (b.value_per_anfo as number) - (a.value_per_anfo as number))[0];
  const marke = (r: ProduktZeile) => (r.pay_rate_flag && r.pay_rate !== null ? `${pct0(r.pay_rate)} ${r.pay_rate_flag === 'up' ? '▲' : '▼'}` : null);
  const faktor = (top.value_per_anfo as number) / schnittJeAnfo;
  const nenne = produktNenner();
  const teile = [
    `${nenne(top.plx_product, top.name)}: Ø ${de0(top.value_per_anfo as number)} € je Anfo (${de1(faktor)}× Schnitt ${de0(schnittJeAnfo)} €)${marke(top) ? `, Bezahlquote ${marke(top)}` : ''}.`,
  ];
  const besser = z.filter((r) => r !== top && r.pay_rate_flag === 'up');
  const schlechter = z.filter((r) => r !== top && r.pay_rate_flag === 'down');
  if (besser.length) teile.push(`Auffällig besser zahlt: ${besser.map((r) => `${nenne(r.plx_product, r.name)} ${marke(r)}`).join(', ')}.`);
  if (schlechter.length) teile.push(`Auffällig schlechter zahlt: ${schlechter.map((r) => `${nenne(r.plx_product, r.name)} ${marke(r)}`).join(', ')}.`);
  return teile.join(' ');
}

// ─── Was 1.000 Kontakte je Produkt erwarten lassen (B6) ─────────────────────────────────────

/** Darunter entscheiden einzelne Anfos (Regel 3): grau unter 20 Anfos oder unter 100.000 Kontakten. */
export const MIN_KONTAKTE_EFF = 100_000;
export const MIN_ANFOS_EFF = 20;
const festeAnfos = (t: EffizienzTeil) => t.anfos - (t.provisional ?? 0);
export const tragfaehig = (t: EffizienzTeil) => t.m4_per_1k !== null && t.contacts >= MIN_KONTAKTE_EFF && festeAnfos(t) >= MIN_ANFOS_EFF;

/** „Direkt-Mailing bringt bei 5 von 5 Produkten mit beiden Wegen mehr je 1.000 Kontakte (z. B. FUM 44 € gegen 19 €)." */
export function effizienzBefund(rows: EffizienzZeile[]): string | null {
  const beide = rows.filter((r) => tragfaehig(r.web) && tragfaehig(r.plx));
  if (!beide.length) return null;
  const dmVorn = beide.filter((r) => (r.plx.m4_per_1k as number) > (r.web.m4_per_1k as number));
  const bsp = [...beide].sort((a, b) => Math.abs((b.plx.m4_per_1k as number) - (b.web.m4_per_1k as number)) - Math.abs((a.plx.m4_per_1k as number) - (a.web.m4_per_1k as number)))[0];
  const beispiel = `z. B. ${produktNenner()(bsp.plx_product, bsp.name)} ${euro1k(bsp.plx.m4_per_1k as number)} über das Direkt-Mailing gegen ${euro1k(bsp.web.m4_per_1k as number)} über die Einladung`;
  const n = beide.length;
  const wort = n === 1 ? 'Produkt' : 'Produkten';
  // Runde 2 (GF): „5 von 5" stand neben 7 Zeilen mit beiden Wegen — die Grenze gehört in den Satz, ebenso dass das
  // Kapitel „Kanäle" anders zuordnet (andere Zahl für Direkt-Mailings).
  const grenze = `mit genug Daten auf beiden Wegen (ab ${MIN_ANFOS_EFF} Anfos und 100.000 Kontakten je Weg)`;
  const zuordnung = 'Zuordnung und Fenster wie diese Karte, nicht wie im Kapitel „Kanäle“.';
  if (dmVorn.length === 0) return `Die Webinar-Einladung bringt bei allen ${n} ${wort} ${grenze} mehr je 1.000 Kontakte (${beispiel}). ${zuordnung}`;
  return `Direkt-Mailing bringt bei ${dmVorn.length} von ${n} ${wort} ${grenze} mehr je 1.000 Kontakte (${beispiel}). Verschiedene Listen — ein Richtwert, kein direkter Tausch. ${zuordnung}`;
}

// ─── Upsell (B7) ────────────────────────────────────────────────────────────────────────────

/** Produkte, bei denen eine Upsell-Anfo deutlich mehr wert ist (Regel der API: Faktor und Mindestzahl Upsells). */
export function upsellStark(d: Pick<UpsellAntwort, 'faktoren' | 'faktor_regel'>) {
  const regel = d.faktor_regel ?? { min_faktor: 1.5, min_upsell_anfos: 20 };
  return (d.faktoren ?? []).filter((x) => x.faktor !== null && x.faktor >= regel.min_faktor && x.upsell_anfos >= regel.min_upsell_anfos);
}

/** „1 Prozentpunkt mehr Upsell ≈ +13,5 T€ Erwarteter Wert im Jahr (Schätzung …)". null ohne Hebel. */
export function upsellBefund(d: Pick<UpsellAntwort, 'faktoren' | 'faktor_regel'> & Partial<Pick<UpsellAntwort, 'products'>>): string | null {
  const mit = upsellStark(d)
    .filter((x) => x.hebel_eur !== null)
    .sort((a, b) => (b.hebel_eur as number) - (a.hebel_eur as number));
  if (!mit.length) return null;
  const summe = mit.reduce((s, x) => s + (x.hebel_eur as number), 0);
  // Namen aus den Produktzeilen derselben Antwort (Audit C3: erste Nennung „Name (Kürzel)").
  const namen = new Map((d.products ?? []).map((p) => [p.plx_product, p.name]));
  const nenne = produktNenner();
  return `1 Prozentpunkt mehr Upsell ≈ +${tE(summe)} Erwarteter Wert im Jahr — Schätzung bei gleicher Anfo-Zahl, gerechnet auf die Anfos der letzten 12 Monate (${mit
    .map((x) => `${nenne(x.plx_product, namen.get(x.plx_product))} +${tE(x.hebel_eur as number)}`)
    .join(' · ')}).`;
}

/**
 * Befund „Wert je Anmeldung" (aus `WertJeAnmeldungKarte.tsx` hierher, Audit C3): höchster gegen niedrigsten Wert je
 * Anmeldung der nicht grauen Produkte, mit „Name (Kürzel)". null unter zwei Produkten.
 */
export function wertJeAnmeldungSatz(
  rows: { plx_product: string; name: string; grau?: boolean; wert_je_anmeldung: number | null; budget_je_anmeldung: number | null }[],
  eur: (v: number | null) => string,
): string | null {
  const ok = rows.filter((r) => !r.grau && r.plx_product && r.wert_je_anmeldung !== null);
  if (ok.length < 2) return null;
  const sortiert = [...ok].sort((a, b) => (b.wert_je_anmeldung as number) - (a.wert_je_anmeldung as number));
  const hoch = sortiert[0];
  const tief = sortiert[sortiert.length - 1];
  const nenne = produktNenner();
  return `Eine Anmeldung lässt bei ${nenne(hoch.plx_product, hoch.name)} ${eur(hoch.wert_je_anmeldung)} erwarten, bei ${nenne(tief.plx_product, tief.name)} ${eur(tief.wert_je_anmeldung)}; vertretbar im Einkauf sind ${eur(hoch.budget_je_anmeldung)} bzw. ${eur(tief.budget_je_anmeldung)}.`;
}

/** Eine Bestellseite gilt als ausgelaufen ohne Anfo in so vielen Tagen. */
export const AUSGELAUFEN_TAGE = 30;
/** Grenztag (YYYY-MM-DD): letzte Anfo davor = ausgelaufen. */
export function ausgelaufenGrenze(asOf: string, tage = AUSGELAUFEN_TAGE): string {
  const t = new Date(`${asOf.slice(0, 10)}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() - tage);
  return t.toISOString().slice(0, 10);
}
